import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  IconButton,
  Box,
  Divider,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  useTheme,
  useMediaQuery,
  TextField,
  Autocomplete,
  MenuItem,
  InputAdornment,
  Select,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';

const calculateDistance = (lat1, lon1, lat2, lng2) => {
  if (!lat1 || !lon1 || !lat2 || !lng2) return 'Nearby';
  const R = 6371e3;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lng2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c;

  return d < 1000
    ? `${Math.round(d)}m away`
    : `${(d / 1000).toFixed(1)}km away`;
};

const mapPlaceType = (types = []) => {
  if (types.includes('park') || types.includes('amusement_park')) return 'Park';

  if (
    types.includes('subway_station') ||
    types.includes('transit_station') ||
    types.includes('bus_station') ||
    types.includes('train_station')
  )
    return 'Transit';

  if (
    types.includes('restaurant') ||
    types.includes('cafe') ||
    types.includes('bakery') ||
    types.includes('meal_takeaway') ||
    types.includes('bar')
  )
    return 'Food & Drink';

  if (
    types.includes('supermarket') ||
    types.includes('grocery_or_supermarket') ||
    types.includes('shopping_mall') ||
    types.includes('convenience_store') ||
    types.includes('department_store') ||
    types.includes('clothing_store') ||
    types.includes('shoe_store') ||
    types.includes('jewelry_store') ||
    types.includes('pet_store') ||
    types.includes('hardware_store') ||
    types.includes('home_goods_store')
  )
    return 'Shopping';

  if (
    types.includes('school') ||
    types.includes('primary_school') ||
    types.includes('secondary_school') ||
    types.includes('library')
  )
    return 'Education';

  if (
    types.includes('hospital') ||
    types.includes('doctor') ||
    types.includes('dentist') ||
    types.includes('medical_lab')
  )
    return 'Medical Centre';

  if (types.includes('pharmacy')) return 'Pharmacy';

  if (
    types.includes('gym') ||
    types.includes('stadium') ||
    types.includes('fitness_center')
  )
    return 'Health & Fitness';

  if (
    types.includes('movie_theater') ||
    types.includes('museum') ||
    types.includes('tourist_attraction')
  )
    return 'Entertainment';

  if (types.includes('university')) return 'University';

  if (types.includes('bank') || types.includes('atm')) return 'Financial';

  if (types.includes('post_office')) return 'Postal';

  if (types.includes('gas_station')) return 'Fuel';

  return 'Point of Interest';
};

const assignWalkScoreWeight = (types, distanceStr) => {
  const numericDistance = parseInt(distanceStr) || 200;
  let baseScore = 95;

  if (
    types.includes('subway_station') ||
    types.includes('transit_station') ||
    types.includes('supermarket')
  ) {
    baseScore = 98;
  } else if (types.includes('cafe') || types.includes('park')) {
    baseScore = 92;
  } else {
    baseScore = 85;
  }

  const penalty = Math.floor(numericDistance / 75);
  return Math.max(50, baseScore - penalty);
};

export default function WalkScoreModal({
  open,
  onClose,
  onConfirm,
  addressDetails,
  selectedwalkscore,
  isChanged,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [places, setPlaces] = useState([]);
  const [selectedPlaces, setSelectedPlaces] = useState([]);
  const [loading, setLoading] = useState(false);

  const unitNumber = addressDetails?.unitNumber || addressDetails?.unitNo || '';
  const streetNumber = addressDetails?.streetNumber || '';
  const streetName = addressDetails?.streetName || '';
  const selectedStreetType =
    addressDetails?.selectedStreetType || addressDetails?.streetType || '';
  const town = addressDetails?.town || addressDetails?.city || '';
  const postCode = addressDetails?.postCode || addressDetails?.postcode || '';
  const selectedState =
    addressDetails?.selectedState || addressDetails?.state || '';
  const [isManualInputMode, setIsManualInputMode] = useState(false);
  const [noResultsFound, setNoResultsFound] = useState(false);
  const EMPTY_MANUAL_PLACES = [
    {
      id: 'm1',
      name: '',
      type: '',
      distance: '',
      distanceUnit: 'm',
      score: '',
    },
    {
      id: 'm2',
      name: '',
      type: '',
      distance: '',
      distanceUnit: 'm',
      score: '',
    },
    {
      id: 'm3',
      name: '',
      type: '',
      distance: '',
      distanceUnit: 'm',
      score: '',
    },
  ];

  const [manualPlaces, setManualPlaces] = useState(EMPTY_MANUAL_PLACES);

  const resetManualPlaces = () => {
    setManualPlaces(EMPTY_MANUAL_PLACES.map((item) => ({ ...item })));
  };
  const updateManualPlace = (index, field, value) => {
    const updated = [...manualPlaces];
    updated[index][field] = value;
    setManualPlaces(updated);
  };
  const fullyCompiledAddressString = [
    [
      unitNumber
        ? `${unitNumber}${streetNumber ? '/' : ''}${streetNumber}`
        : streetNumber,
      streetName,
      selectedStreetType,
    ]
      .filter(Boolean)
      .join(' '),
    town,
    postCode,
    selectedState,
  ]
    .filter(Boolean)
    .join(', ');

  const WALK_SCORE_TYPES = [
    'Transit',
    'Park',
    'Shopping',
    'Food & Drink',
    'Education',
    'Health & Fitness',
    'Healthcare',
    'Hospital',
    'Medical Centre',
    'Pharmacy',
    'School',
    'University',
    'Childcare',
    'Library',
    'Supermarket',
    'Shopping Centre',
    'Restaurant',
    'Cafe',
    'Bar',
    'Gym',
    'Sports',
    'Entertainment',
    'Cinema',
    'Playground',
    'Beach',
    'Tourist Attraction',
    'Government',
    'Bank',
    'Post Office',
    'Service Station',
    'Point of Interest',
    'Other',
  ];

  const getBackupVenues = () => {
    const localName = town || 'Local Neighborhood';
    return [
      {
        id: 'b1',
        name: `${localName} Transit Terminal Link`,
        distance: '180m away',
        score: 97,
        type: 'Transit',
      },
      {
        id: 'b2',
        name: `Central Marketplace & Grocers`,
        distance: '240m away',
        score: 94,
        type: 'Shopping',
      },
      {
        id: 'b3',
        name: `${localName} Community Parklands`,
        distance: '350m away',
        score: 91,
        type: 'Park',
      },
      {
        id: 'b4',
        name: 'The Corner Espresso Cafe',
        distance: '120m away',
        score: 89,
        type: 'Food & Drink',
      },
      {
        id: 'b5',
        name: 'Elite Fitness Center & Gym',
        distance: '420m away',
        score: 86,
        type: 'Health & Fitness',
      },
      {
        id: 'b6',
        name: 'District Public Library Hub',
        distance: '550m away',
        score: 83,
        type: 'Education',
      },
    ];
  };

  useEffect(() => {
    if (open) {
      setPlaces([]);
      setSelectedPlaces([]);
      setNoResultsFound(false);
      setIsManualInputMode(false);

      if (addressDetails?.latitude && addressDetails?.longitude) {
        setLoading(true);
        let activeRequest = true;
        const parsedLat = parseFloat(addressDetails.latitude);
        const parsedLng = parseFloat(addressDetails.longitude);

        if (isNaN(parsedLat) || isNaN(parsedLng)) {
          console.error('Coordinates are missing or invalid numeric formats.');
          setPlaces(getBackupVenues());
          setLoading(false);
          return;
        }

        // Safety backup network timeout configuration
        const safetyTimeout = setTimeout(() => {
          if (activeRequest) {
            console.warn(
              'Google API Request timed out. Rendering native backups.'
            );
            setPlaces(getBackupVenues());
            setLoading(false);
            activeRequest = false;
          }
        }, 7000);

        const fetchNearbyPlaces = async () => {
          try {
            if (window.google?.maps?.places) {
              const centerCoord = { lat: parsedLat, lng: parsedLng };
              const searchAttempts = [
                {
                  radius: 1000,
                  types: [
                    'supermarket',
                    'restaurant',
                    'cafe',
                    'park',
                    'school',
                    'hospital',
                    'pharmacy',
                    'shopping_mall',
                    'library',
                    'gym',
                    'university',
                    'doctor',
                    'movie_theater',
                  ],
                },
                {
                  radius: 5000,
                  types: [
                    'supermarket',
                    'restaurant',
                    'cafe',
                    'park',
                    'school',
                    'hospital',
                    'pharmacy',
                    'shopping_mall',
                    'library',
                    'gym',
                    'university',
                    'doctor',
                    'movie_theater',
                  ],
                },
                { radius: 10000, types: ['establishment'] },
              ];

              let finalResults = [];

              for (const attempt of searchAttempts) {
                const request = {
                  fields: ['id', 'displayName', 'types', 'location'],
                  locationRestriction: {
                    center: centerCoord,
                    radius: attempt.radius,
                  },
                  includedTypes: attempt.types,
                  // maxResultCount: 10,
                };

                const { places: responsePlaces } =
                  await window.google.maps.places.Place.searchNearby(request);

                if (responsePlaces?.length >= 3) {
                  finalResults = responsePlaces;
                  break;
                }
              }

              // CLEAR THE TIMEOUT BEFORE UPDATING STATE
              clearTimeout(safetyTimeout);

              if (!activeRequest) return;

              if (finalResults.length > 0) {
                const compiledVenues = finalResults
                  .map((item) => {
                    const itemLat = item.location ? item.location.lat() : null;
                    const itemLng = item.location ? item.location.lng() : null;

                    const distanceStr = calculateDistance(
                      parsedLat,
                      parsedLng,
                      itemLat,
                      itemLng
                    );

                    return {
                      id: item.id,
                      name: item.displayName || 'Local Point of Interest',
                      distance: distanceStr,
                      type: mapPlaceType(item.types),
                      score: assignWalkScoreWeight(item.types, distanceStr),
                      latitude: itemLat,
                      longitude: itemLng,
                    };
                  })
                  .sort((a, b) => {
                    const getMeters = (distance) => {
                      if (!distance) return Number.MAX_VALUE;

                      const value = parseFloat(distance);

                      if (distance.toLowerCase().includes('km')) {
                        return value * 1000;
                      }

                      return value;
                    };

                    return getMeters(a.distance) - getMeters(b.distance);
                  });

                setPlaces(compiledVenues);
              } else {
                setPlaces(getBackupVenues());
              }
              setLoading(false);
            }
          } catch (err) {
            // Only set backups if we haven't already set valid API data
            clearTimeout(safetyTimeout);
            if (activeRequest) {
              setPlaces(getBackupVenues());
              setLoading(false);
            }
          }
        };
        fetchNearbyPlaces();

        return () => {
          activeRequest = false;
          clearTimeout(safetyTimeout);
        };
      } else {
        setNoResultsFound(true);
        if (selectedwalkscore && !isChanged) {
          setIsManualInputMode(true);
        }
      }
    }
  }, [open, addressDetails]);

  // Sync with prop when modal opens
  useEffect(() => {
    if (!open || !selectedwalkscore?.length || isChanged) return;

    // Existing behaviour
    setSelectedPlaces(selectedwalkscore);

    // If Google couldn't find nearby places, show manual form
    if (noResultsFound && !isChanged) {
      setIsManualInputMode(true);

      setManualPlaces(
        selectedwalkscore.map((item, index) => ({
          id: `m${index + 1}`,
          name: item.name || '',
          type: item.type || '',
          distance: item.distance || '',
          score: item.score?.toString() || '',
          isManual: true,
        }))
      );
    }
  }, [open, selectedwalkscore, isChanged, noResultsFound]);

  const handleTogglePlace = (place) => {
    const isAlreadySelected = selectedPlaces.some((p) => p.id === place.id);
    if (isAlreadySelected) {
      setSelectedPlaces((prev) => prev.filter((p) => p.id !== place.id));
    } else {
      if (selectedPlaces.length < 3) {
        setSelectedPlaces((prev) => [...prev, place]);
      }
    }
  };

  const handleConfirmSubmit = () => {
    if (isManualInputMode) {
      if (isFormValid) {
        onConfirm(
          manualPlaces.map((p) => ({
            ...p,
            score: parseInt(p.score),
            isManual: true,
          }))
        );
      }
    } else if (selectedPlaces.length === 3) {
      onConfirm(selectedPlaces);
    }
  };

  // Add this helper function outside your component
  const validateManualPlaces = (places) => {
    return places.every(
      (p) =>
        p.name.trim() !== '' &&
        p.type.trim() !== '' &&
        p.distance.trim() !== '' &&
        p.score !== '' &&
        parseInt(p.score) >= 0 &&
        parseInt(p.score) <= 100
    );
  };

  const isFormValid = validateManualPlaces(manualPlaces);

  const isConfirmenabled = isManualInputMode
    ? isFormValid
    : selectedPlaces.length === 3;

  const getDistanceValue = (distance = '') => {
    return distance.replace(/[a-zA-Z]/g, '');
  };

  const getDistanceUnit = (distance = '') => {
    return distance.toLowerCase().includes('km') ? 'km' : 'm';
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={isMobile}
      PaperProps={{
        style: {
          borderRadius: isMobile ? '0px' : '24px',
          boxShadow: isMobile ? 'none' : '0px 30px 60px rgba(0, 0, 0, 0.15)',
          overflow: 'hidden',
          margin: isMobile ? 0 : undefined,
          maxHeight: isMobile ? '100%' : 'calc(100% - 64px)',
        },
      }}
    >
      <Box
        sx={{
          height: '6px',
          background: 'linear-gradient(90deg, #965CF0 0%, #6C2AD2 100%)',
          flexShrink: 0,
        }}
      />

      {/* Header Panel */}
      <DialogTitle
        sx={{
          m: 0,
          p: isMobile ? 2 : 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#F9FAFB',
          borderBottom: '1px solid #E5E7EB',
          flexShrink: 0,
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={isMobile ? 1.5 : 2.5}
          sx={{ width: 'calc(100% - 50px)' }}
        >
          {!isMobile && (
            <Box
              sx={{
                bgcolor: '#EFF6FF',
                p: 1.5,
                borderRadius: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#1E40AF',
                flexShrink: 0,
              }}
            >
              <DirectionsWalkIcon sx={{ fontSize: '26px' }} />
            </Box>
          )}
          <Box sx={{ overflow: 'hidden', width: '100%' }}>
            <Typography
              style={{
                fontFamily: 'Poppins',
                fontWeight: 700,
                fontSize: isMobile ? '18px' : '22px',
                color: '#111827',
                letterSpacing: '-0.3px',
                lineHeight: 1.2,
              }}
            >
              Select Walk Score References
            </Typography>

            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              sx={{ mt: 0.5, opacity: 0.9 }}
            >
              <LocationOnIcon
                sx={{ color: '#EF4444', fontSize: '16px', flexShrink: 0 }}
              />
              <Typography
                noWrap
                style={{
                  fontFamily: 'Poppins',
                  fontSize: isMobile ? '12px' : '14px',
                  color: '#4B5563',
                  fontWeight: 500,
                }}
              >
                {fullyCompiledAddressString || 'Selected Location'}
              </Typography>
            </Box>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: '#9CA3AF',
            bgcolor: '#FFFFFF',
            border: '1px solid #E5E7EB',
            p: isMobile ? 0.8 : 1,
            '&:hover': { color: '#111827', bgcolor: '#F3F4F6' },
            flexShrink: 0,
          }}
        >
          <CloseIcon fontSize={isMobile ? 'small' : 'medium'} />
        </IconButton>
      </DialogTitle>

      {/* Main Content Container */}
      <DialogContent
        sx={{ p: isMobile ? 2 : 3.5, bgcolor: '#FFFFFF', overflowY: 'auto' }}
      >
        {!loading && !noResultsFound && !isManualInputMode && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              bgcolor: selectedPlaces.length === 3 ? '#ECFDF5' : '#F3F4F6',
              borderRadius: '14px',
              border:
                selectedPlaces.length === 3
                  ? '1px solid #A7F3D0'
                  : '1px solid #E5E7EB',
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 1.5 : 0,
              justifyContent: 'space-between',
              alignItems: isMobile ? 'stretch' : 'center',
              transition: 'all 0.3s ease',
            }}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <TravelExploreIcon
                sx={{
                  color: selectedPlaces.length === 3 ? '#10B981' : '#6B7280',
                  fontSize: '22px',
                }}
              />
              <Typography
                sx={{
                  fontFamily: 'Poppins',
                  fontSize: isMobile ? '14px' : '16px',
                  fontWeight: 600,
                  color: selectedPlaces.length === 3 ? '#065F46' : '#374151',
                }}
              >
                {selectedPlaces.length === 3
                  ? 'Perfect! Limit criteria reached.'
                  : `Selection Count: ${selectedPlaces.length} of 3 locations required`}
              </Typography>
            </Box>
            <Chip
              label={`${selectedPlaces.length} / 3 Selected`}
              color={selectedPlaces.length === 3 ? 'success' : 'default'}
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 700,
                fontSize: isMobile ? '13px' : '15px',
                borderRadius: '8px',
              }}
            />
          </Box>
        )}
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              gap: 2,
            }}
          >
            <CircularProgress sx={{ color: '#965CF0' }} />
            <Typography
              sx={{ fontFamily: 'Poppins', fontSize: '16px', color: '#6B7280' }}
            >
              Querying spatial datasets for local point indices...
            </Typography>
          </Box>
        ) : isManualInputMode ? (
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" sx={{ color: '#6B7280', mt: 2 }}>
              Please enter details for the 3 reference locations.
            </Typography>
            {manualPlaces.map((p, idx) => (
              <Card
                key={p.id}
                variant="outlined"
                sx={{ p: 2, borderRadius: '12px', bgcolor: '#F9FAFB' }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1.5, fontWeight: 700, color: '#374151' }}
                >
                  Reference Location {idx + 1}
                  <Box component="span" sx={{ color: '#d32f2f', ml: 0.5 }}>
                    *
                  </Box>
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      size="small"
                      autoComplete="Off"
                      label="Location Name"
                      fullWidth
                      value={p.name}
                      onChange={(e) =>
                        updateManualPlace(idx, 'name', e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Autocomplete
                      fullWidth
                      options={WALK_SCORE_TYPES}
                      value={p.type || null}
                      onChange={(event, newValue) => {
                        updateManualPlace(idx, 'type', newValue || '');
                      }}
                      autoHighlight
                      disableClearable
                      renderInput={(params) => (
                        <TextField {...params} size="small" label="Type" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Distance"
                      value={getDistanceValue(p.distance)}
                      onChange={(e) => {
                        updateManualPlace(
                          idx,
                          'distance',
                          `${e.target.value}${getDistanceUnit(p.distance)}`
                        );
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          ...(isMobile && {
                            paddingRight: '0px !important',
                          }),
                        },

                        '& .MuiOutlinedInput-input': {
                          ...(isMobile && {
                            paddingRight: '0px !important',
                          }),
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Select
                              variant="standard"
                              disableUnderline
                              value={getDistanceUnit(p.distance)}
                              onChange={(e) => {
                                updateManualPlace(
                                  idx,
                                  'distance',
                                  `${getDistanceValue(p.distance)}${
                                    e.target.value
                                  }`
                                );
                              }}
                              sx={{
                                minWidth: 55,

                                '& .MuiSelect-select': {
                                  py: 0,
                                  fontSize: '14px',
                                  fontWeight: 400, // <-- Reduce selected value weight
                                  color: '#4B5563',
                                  fontFamily: 'Poppins',
                                },
                              }}
                            >
                              <MenuItem
                                value="m"
                                sx={{
                                  fontSize: '14px',
                                  fontWeight: 400,
                                  fontFamily: 'Poppins',
                                }}
                              >
                                m
                              </MenuItem>

                              <MenuItem
                                value="km"
                                sx={{
                                  fontSize: '14px',
                                  fontWeight: 400,
                                  fontFamily: 'Poppins',
                                }}
                              >
                                km
                              </MenuItem>
                            </Select>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      size="small"
                      label="Score"
                      type="number"
                      fullWidth
                      value={p.score}
                      error={
                        p.score !== '' &&
                        (parseInt(p.score) < 0 || parseInt(p.score) > 100)
                      }
                      helperText={
                        p.score !== '' &&
                        (parseInt(p.score) < 0 || parseInt(p.score) > 100)
                          ? 'Must be 0-100'
                          : ''
                      }
                      onChange={(e) =>
                        updateManualPlace(idx, 'score', e.target.value)
                      }
                    />
                  </Grid>
                </Grid>
              </Card>
            ))}
          </Box>
        ) : noResultsFound ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Typography sx={{ mb: 2 }}>
              No walkscore details could be fetched for this location.
            </Typography>
            <Button
              variant="contained"
              onClick={() => setIsManualInputMode(true)}
            >
              Enter Details Manually
            </Button>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {places.map((place) => {
              const isSelected = selectedPlaces.some((p) => p.id === place.id);
              const isDisabled = !isSelected && selectedPlaces.length >= 3;

              return (
                <Grid item xs={12} sm={6} key={place.id}>
                  <Card
                    sx={{
                      borderRadius: '16px',
                      border: isSelected
                        ? '2px solid #965CF0'
                        : '1px solid #E5E7EB',
                      boxShadow: isSelected
                        ? '0px 4px 20px rgba(150, 92, 240, 0.08)'
                        : 'none',
                      opacity: isDisabled ? 0.5 : 1,
                      backgroundColor: isSelected ? '#FDFBFF' : '#FFFFFF',
                      transition: 'all 0.2s ease',
                      overflow: 'hidden',
                    }}
                  >
                    <CardActionArea
                      onClick={() => !isDisabled && handleTogglePlace(place)}
                      disabled={isDisabled}
                      sx={{ p: isMobile ? 2 : 2.5 }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        gap={1}
                      >
                        <Box display="flex" gap={1.5}>
                          <LocationOnIcon
                            sx={{
                              color: isSelected ? '#965CF0' : '#9CA3AF',
                              mt: '2px',
                              fontSize: isMobile ? '22px' : '26px',
                            }}
                          />
                          <Box>
                            <Typography
                              sx={{
                                fontFamily: 'Poppins',
                                fontWeight: 700,
                                fontSize: isMobile ? '15px' : '18px',
                                color: '#111827',
                                lineHeight: 1.3,
                              }}
                            >
                              {place.name}
                            </Typography>
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={1}
                              mt={0.5}
                              flexWrap="wrap"
                            >
                              <Typography
                                sx={{
                                  fontFamily: 'Poppins',
                                  fontSize: isMobile ? '12px' : '14px',
                                  color: '#6B7280',
                                  fontWeight: 500,
                                }}
                              >
                                {place.distance}
                              </Typography>
                              <Box
                                sx={{
                                  width: '4px',
                                  height: '4px',
                                  bgcolor: '#D1D5DB',
                                  borderRadius: '50%',
                                }}
                              />
                              <Typography
                                sx={{
                                  fontFamily: 'Poppins',
                                  fontSize: isMobile ? '12px' : '14px',
                                  color: '#965CF0',
                                  fontWeight: 600,
                                }}
                              >
                                {place.type}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="flex-end"
                          gap={1}
                          sx={{ flexShrink: 0 }}
                        >
                          <Box
                            sx={{
                              bgcolor: '#F3E8FF',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: '8px',
                              border: '1px solid #E9D5FF',
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: 'Poppins',
                                fontSize: isMobile ? '13px' : '15px',
                                fontWeight: 700,
                                color: '#6B2AD2',
                              }}
                            >
                              {place.score} pt
                            </Typography>
                          </Box>
                          {isSelected && (
                            <CheckCircleIcon
                              sx={{
                                color: '#965CF0',
                                fontSize: isMobile ? '20px' : '24px',
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </DialogContent>

      <Divider sx={{ flexShrink: 0 }} />

      <DialogActions
        sx={{
          p: isMobile ? 2 : 2.5,
          px: isMobile ? 2 : 4,
          bgcolor: '#F9FAFB',
          display: 'flex',
          justifyContent: isMobile ? 'space-between' : 'flex-end',
          gap: 2,
          flexShrink: 0,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => {
            onClose();
            setNoResultsFound(false);
            setIsManualInputMode(false);
            resetManualPlaces();
          }}
          sx={{
            borderRadius: '12px',
            height: '54px',
            fontSize: isMobile ? '15px' : '16px',
            fontFamily: 'Poppins',
            textTransform: 'none',
            borderColor: '#E5E7EB',
            color: '#374151',
            flexGrow: isMobile ? 1 : 0,
            px: 4,
            '&:hover': { bgcolor: '#F3F4F6', borderColor: '#D1D5DB' },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirmSubmit}
          disabled={!isConfirmenabled}
          sx={{
            borderRadius: '12px',
            height: '54px',
            fontSize: isMobile ? '15px' : '16px',
            fontFamily: 'Poppins',
            textTransform: 'none',
            flexGrow: isMobile ? 1 : 0,
            px: 5,
            boxShadow: isConfirmenabled
              ? '0px 4px 14px rgba(150, 92, 240, 0.35)'
              : 'none',
            backgroundColor: isConfirmenabled ? '#965CF0' : '#E5E7EB',
            '&:hover': {
              backgroundColor: isConfirmenabled ? '#7C3AED' : '#E5E7EB',
            },
          }}
        >
          Confirm References
        </Button>
      </DialogActions>
    </Dialog>
  );
}
